import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus, Trash2, FileEdit, ExternalLink, Save } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/api";
import { BRAND_COLORS } from "../lib/bookingStatus";

/**
 * Feb 2026 · 10 Feb 2026 · Admin-Seite „Rechnungsvorlage".
 *
 * User-Directive: „Admin soll die Rechnungsvorlage anpassen können —
 * manuell (Logo, Firmenname, Adresse, Steuernummer, Zahlungsdetails,
 * Fußzeile) ODER eine bestehende PDF hochladen." Diese erste Version
 * deckt den manuellen Editor voll ab; die PDF-Digitalisierung wurde vom
 * User bewusst nicht mitbestellt.
 *
 * Backend-Endpoints:
 *   GET /api/team/company-settings      — read
 *   PUT /api/team/company-settings      — save (admin-only)
 *   POST /api/files/upload              — Logo-Upload (returns {id})
 *
 * Werte fliessen live in den Rechnungs-PDF-Renderer
 * (`routers/invoices.py::_render_invoice_pdf`). Bestehende PDFs im
 * Archiv bleiben unangetastet — nur zukünftige Rechnungen nutzen die
 * neuen Werte.
 */
export default function TeamInvoiceTemplate() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Feb 2026 · 10 Feb 2026 · Sofortige Logo-Live-Preview.
  // Direkt nach dem Datei-Pick zeigen wir die lokale Blob-URL —
  // instant feedback, kein Server-Roundtrip, keine Auth-Frage. Der Blob
  // bleibt aktiv bis zum nächsten Reload/Unmount.
  const [localLogoUrl, setLocalLogoUrl] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/team/company-settings");
      // If DB has neither zip nor city, derive from legacy `zip_city`
      // ("82041 Oberbiberg") so the split fields are populated in the UI.
      const zip = data.zip || (data.zip_city || "").split(" ")[0] || "";
      const city = data.city || (data.zip_city || "").split(" ").slice(1).join(" ") || "";
      setForm({
        logo_file_id: data.logo_file_id || "",
        name: data.name || "",
        short_name: data.short_name || "",
        street: data.street || "",
        zip,
        city,
        country: data.country || "",
        vat_id: data.vat_id || "",
        tax_number: data.tax_number || "",
        bank_name: data.bank_name || "",
        iban: data.iban || "",
        bic: data.bic || "",
        email: data.email || "",
        phone: data.phone || "",
        web: data.web || "",
        footer_text: data.footer_text || "",
        invoice_archive_email: data.invoice_archive_email || "",
      });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      toast.error("Bitte PNG, JPG, WEBP oder SVG hochladen.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Logo darf max. 4 MB gross sein.");
      return;
    }
    // Sofortige lokale Preview via Blob-URL. Alte URL erst nach dem
    // State-Update revoken, damit das <img> nicht kurz „defekt" flackert.
    const nextUrl = URL.createObjectURL(file);
    setLocalLogoUrl((prev) => {
      if (prev) { try { URL.revokeObjectURL(prev); } catch { /* ignore */ } }
      return nextUrl;
    });
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patch("logo_file_id", data.id);
      toast.success("Logo hochgeladen. Zum Übernehmen bitte 'Speichern' klicken.");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  // Blob-URL nach Unmount aufräumen (Memory-Hygiene).
  useEffect(() => {
    return () => { if (localLogoUrl) { try { URL.revokeObjectURL(localLogoUrl); } catch { /* ignore */ } } };
  }, [localLogoUrl]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      await api.put("/team/company-settings", form);
      toast.success("Rechnungsvorlage gespeichert.");
      await load();
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const previewPdf = async () => {
    // Öffnet die Preview-PDF eines minimal-erforderlichen Payloads —
    // Endpoint `POST /invoices/preview-pdf` liefert einen 1-Seiter mit
    // Wasserzeichen „VORSCHAU". Nutzt exakt den gleichen Renderer wie
    // finale Rechnungen und zeigt daher die Vorlagen-Werte 1:1.
    try {
      // Pick any customer + property for the preview render.
      const cust = await api.get("/team/customers");
      const c = (cust.data || [])[0];
      if (!c) { toast.error("Kein Kunde für Vorschau vorhanden."); return; }
      const props = await api.get("/team/properties");
      const p = (props.data || []).find((x) => x.customer_id === c.id) || (props.data || [])[0];
      const body = {
        customer_id: c.id,
        property_id: p?.id || null,
        subject: "Vorschau",
        salutation: "Sehr geehrte Damen und Herren,",
        intro_text: "dies ist eine Vorschau Ihrer Rechnungsvorlage.",
        line_items: [{ description: "Beispiel-Position", qty: 1, unit_price: 100.0 }],
        vat_rate: 19,
      };
      const res = await api.post("/invoices/preview-pdf", body, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message || "Vorschau fehlgeschlagen.");
    }
  };

  if (!form) {
    return (
      <div className="max-w-[860px] mx-auto px-4 md:px-10 py-12" data-testid="team-invoice-template">
        <Loader2 className="w-5 h-5 animate-spin text-navy-dark/50" />
      </div>
    );
  }

  const logoSrc = localLogoUrl
    ? localLogoUrl
    : form.logo_file_id
      ? fileUrl(form.logo_file_id)
      : "/weissblom-logo.webp";

  return (
    <div className="max-w-[860px] mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="team-invoice-template">
      <div className="mb-8">
        <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Weissblom · Admin</div>
        <h1 className="font-display text-3xl md:text-4xl text-navy leading-tight">Rechnungsvorlage</h1>
        <p className="mt-3 text-[13.5px] text-navy-dark/70 leading-snug max-w-[600px]">
          Diese Felder erscheinen in Kopf, Fuß und Meta-Block jeder neu erstellten Rechnung.
          Bestehende Rechnungs-PDFs im Archiv behalten ihr Aussehen — nur zukünftige Rechnungen
          nutzen die neuen Werte.
        </p>
      </div>

      {/* Logo */}
      <Section title="Logo">
        <div className="flex items-start gap-6">
          <div
            className="w-48 h-24 bg-white border border-navy/10 flex items-center justify-center overflow-hidden"
            data-testid="template-logo-preview"
          >
            <img
              src={logoSrc}
              alt="Logo-Vorschau"
              className="max-w-full max-h-full object-contain"
              onError={(e) => { e.currentTarget.src = "/weissblom-logo.webp"; }}
            />
          </div>
          <div className="flex-1 space-y-2 text-[12px] text-navy-dark/70">
            <p>PNG · JPG · WEBP · SVG · max. 4 MB. Empfohlen: transparenter Hintergrund, breiter als hoch.</p>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="template-logo-upload-btn"
                className="inline-flex items-center gap-1.5 border border-navy/25 hover:bg-navy hover:text-ivory px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                Logo hochladen
              </button>
              {form.logo_file_id && (
                <button
                  type="button"
                  onClick={() => {
                    patch("logo_file_id", "");
                    setLocalLogoUrl((prev) => {
                      if (prev) { try { URL.revokeObjectURL(prev); } catch { /* ignore */ } }
                      return null;
                    });
                  }}
                  data-testid="template-logo-remove-btn"
                  className="inline-flex items-center gap-1.5 border border-navy/15 text-navy-dark/70 hover:text-red-700 px-3 py-1.5 text-[11px] tracking-wider uppercase"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Entfernen
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => uploadLogo(e.target.files?.[0])}
              className="hidden"
              data-testid="template-logo-input"
            />
          </div>
        </div>
      </Section>

      {/* Firma + Adresse */}
      <Section title="Firmenname & Adresse">
        <Field label="Firmenname (voll)">
          <input value={form.name} onChange={(e) => patch("name", e.target.value)}
            data-testid="template-name" className={inputCls} />
        </Field>
        <Field label="Kurzform (Absender-Zeile)">
          <input value={form.short_name} onChange={(e) => patch("short_name", e.target.value)}
            data-testid="template-short-name" className={inputCls} />
        </Field>
        <Field label="Straße">
          <input value={form.street} onChange={(e) => patch("street", e.target.value)}
            data-testid="template-street" className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="PLZ">
            <input value={form.zip} onChange={(e) => patch("zip", e.target.value)}
              data-testid="template-zip" className={inputCls} />
          </Field>
          <Field label="Ort">
            <input value={form.city} onChange={(e) => patch("city", e.target.value)}
              data-testid="template-city" className={inputCls} />
          </Field>
        </div>
        <Field label="Land">
          <input value={form.country} onChange={(e) => patch("country", e.target.value)}
            data-testid="template-country" className={inputCls} />
        </Field>
      </Section>

      {/* Steuer */}
      <Section title="USt-ID & Steuernummer">
        <Field label="USt-IdNr.">
          <input value={form.vat_id} onChange={(e) => patch("vat_id", e.target.value)}
            data-testid="template-vat-id" className={inputCls} placeholder="DE123456789" />
        </Field>
        <Field label="Steuernummer">
          <input value={form.tax_number} onChange={(e) => patch("tax_number", e.target.value)}
            data-testid="template-tax-number" className={inputCls} />
        </Field>
      </Section>

      {/* Zahlungsdetails */}
      <Section title="Zahlungsdetails (IBAN / BIC / Bank)">
        <Field label="Bank">
          <input value={form.bank_name} onChange={(e) => patch("bank_name", e.target.value)}
            data-testid="template-bank-name" className={inputCls} />
        </Field>
        <Field label="IBAN">
          <input value={form.iban} onChange={(e) => patch("iban", e.target.value.toUpperCase())}
            data-testid="template-iban" className={inputCls + " font-mono tracking-wider"} />
        </Field>
        <Field label="BIC">
          <input value={form.bic} onChange={(e) => patch("bic", e.target.value.toUpperCase())}
            data-testid="template-bic" className={inputCls + " font-mono"} />
        </Field>
      </Section>

      {/* Kontakt */}
      <Section title="Kontakt (E-Mail / Telefon / Web)">
        <Field label="E-Mail">
          <input type="email" value={form.email} onChange={(e) => patch("email", e.target.value)}
            data-testid="template-email" className={inputCls} />
        </Field>
        <Field label="Telefon">
          <input value={form.phone} onChange={(e) => patch("phone", e.target.value)}
            data-testid="template-phone" className={inputCls} />
        </Field>
        <Field label="Web">
          <input value={form.web} onChange={(e) => patch("web", e.target.value)}
            data-testid="template-web" className={inputCls} placeholder="www.example.de" />
        </Field>
      </Section>

      {/* Rechnungs-Archiv */}
      <Section title="Rechnungs-Archiv (Kopie per E-Mail)">
        <p className="text-[12px] text-navy-dark/60 -mt-2 mb-2 max-w-[560px]">
          Bei jeder neuen Kundenrechnung (Erstellen, Duplizieren, Storno-Gutschrift, PDF-Upload,
          Serien-Freigabe, Zuordnung) geht automatisch eine Kopie mit dem Original-PDF an diese
          Adresse. Betreff: „Weissblom · Rechnung [Nr.] · Kd-Nr. [Kundennummer]“.
        </p>
        <Field label="Archiv-E-Mail">
          <input type="email" value={form.invoice_archive_email}
            onChange={(e) => patch("invoice_archive_email", e.target.value)}
            data-testid="template-archive-email" className={inputCls}
            placeholder="rechnungen@weissblom.de" />
        </Field>
      </Section>

      {/* Fußzeile */}
      <Section title="Fußzeile-Freitext">
        <p className="text-[12px] text-navy-dark/60 -mt-2 mb-2 max-w-[560px]">
          Erscheint als schmaler zentrierter Zusatz unter dem 4-Spalten-Block (Anschrift · Kontakt ·
          Rechtliches · Bank). Max. ca. 180 Zeichen. Eignet sich für Geschäftsführung, Amtsgericht
          oder einen kurzen Dankeshinweis.
        </p>
        <textarea
          value={form.footer_text}
          onChange={(e) => patch("footer_text", e.target.value)}
          data-testid="template-footer-text"
          rows={3}
          maxLength={240}
          className={inputCls + " resize-y min-h-[72px] font-sans"}
          placeholder="z. B. Geschäftsführung: R. Keersmaekers · Amtsgericht München · Vielen Dank für Ihr Vertrauen."
        />
        <div className="text-right text-[11px] text-navy-dark/50 mt-1">
          {form.footer_text.length} / 240
        </div>
      </Section>

      {/* Actions */}
      <div className="sticky bottom-0 -mx-4 md:mx-0 bg-ivory/95 backdrop-blur border-t border-navy/10 px-4 md:px-0 py-4 flex items-center justify-between gap-4 flex-wrap">
        <button
          type="button"
          onClick={previewPdf}
          data-testid="template-preview-btn"
          className="inline-flex items-center gap-1.5 text-navy border-b border-gold/60 hover:border-gold text-[12px] tracking-wider uppercase pb-0.5"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Vorschau als PDF
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="template-save-btn"
          className="inline-flex items-center gap-1.5 bg-navy text-ivory hover:bg-navy-dark disabled:opacity-60 px-5 py-2.5 text-[12px] tracking-wider uppercase"
          style={{ background: BRAND_COLORS.navy }}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Speichern
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-white border border-navy/25 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-navy/10 p-5 md:p-6 mb-6">
      <h2 className="font-display text-lg text-navy mb-4 flex items-center gap-2">
        <FileEdit className="w-4 h-4 text-navy-dark/60" strokeWidth={1.8} />
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11.5px] uppercase tracking-wider text-navy-dark/60 mb-1">{label}</span>
      {children}
    </label>
  );
}
